import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface TimeZoneContextValue {
  /** Effective IANA zone resolved by the server: user preference -> school -> organization -> system. */
  timeZone: string;
  /** Where the effective zone came from, for display ("School time", "Your preference", ...). */
  source: 'user' | 'school' | 'organization' | 'system';
  locale: string | undefined;
}

const DEFAULT_VALUE: TimeZoneContextValue = {
  timeZone: 'UTC',
  source: 'system',
  locale: undefined,
};

const TimeZoneContext = createContext<TimeZoneContextValue>(DEFAULT_VALUE);

export function TimeZoneProvider({
  timeZone,
  source,
  locale,
  children,
}: Partial<TimeZoneContextValue> & { children: ReactNode }) {
  const value = useMemo<TimeZoneContextValue>(
    () => ({
      timeZone: timeZone ?? DEFAULT_VALUE.timeZone,
      source: source ?? DEFAULT_VALUE.source,
      locale,
    }),
    [timeZone, source, locale],
  );
  return <TimeZoneContext.Provider value={value}>{children}</TimeZoneContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTimeZone(): TimeZoneContextValue {
  return useContext(TimeZoneContext);
}
